import React, { useState, FunctionComponent } from "react"
import {
    TableRow,
    TableCell,
    IconButton,
    Collapse,
    Checkbox,
    CircularProgress,
    Tooltip
} from "@material-ui/core"
import styles from "./PromptRow.module.scss"
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp"
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown"
import DeleteOutline from "@material-ui/icons/DeleteOutline"
import EditIcon from "@material-ui/icons/Edit"
import { Language, Prompt } from "../../../typings/types"
import { PromptCard } from "./PromptCard"
import { TimeStamp } from "../../components/TimeStamp"
import { useSelectedPrompts } from "./hooks/useSelectedPrompts"
import { EditFileModal } from "./edit-file-modal/EditFileModal"

interface PromptRowProps {
    prompt: Prompt
    rowIndex: number
    handleDelete: () => void
}

const NO_LANGUAGES = "N/A"

export const PromptRow: FunctionComponent<PromptRowProps> = props => {
    const { prompt, handleDelete, rowIndex } = props
    const [open, setOpen] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [editing, setEditing] = useState(false)
    const { isSelected, addPrompt, removePrompt } = useSelectedPrompts()

    const handleSelect = () => {
        if (isSelected(prompt.audioLocation)) {
            removePrompt(prompt.audioLocation)
        } else {
            addPrompt(prompt.audioLocation)
        }
    }

    const languagesAsString = (languages: Language[] | undefined): string => {
        if (!languages || languages.length === 0) return NO_LANGUAGES
        return languages.map(l => l.name).join(", ")
    }

    const LanguagesCell = (): JSX.Element => {
        const languages = languagesAsString(prompt.languages)
        const noLanguages = languages === NO_LANGUAGES
        return (
            <TableCell id={"languageName-" + rowIndex}>
                <Tooltip
                    id={`language-tooltip-${rowIndex}`}
                    arrow
                    placement="top"
                    title={noLanguages ? "" : languages}
                >
                    <span
                        data-testid={`language`}
                        className={`${styles.Cell__medium} ${styles.Cell__ellipsis}`}
                    >
                        {languages}
                    </span>
                </Tooltip>
            </TableCell>
        )
    }

    return (
        <React.Fragment>
            <TableRow>
                <TableCell>
                    <span className={styles.Cell__small}>
                        <Checkbox
                            className={"prompt-select-box"}
                            inputProps={{ id: `select_${rowIndex}` }}
                            checked={isSelected(prompt.audioLocation)}
                            onClick={handleSelect}
                            data-testid={`bulkActionCheckbox_${rowIndex}`}
                        />
                    </span>
                </TableCell>
                <TableCell>
                    <span className={styles.Cell__small}>
                        <IconButton
                            size="small"
                            onClick={() => setOpen(!open)}
                            data-testid={"openButton-" + rowIndex}
                            id={"openButton-" + rowIndex}
                            className="openButton"
                        >
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </span>
                </TableCell>
                <TableCell id={"name-" + rowIndex} data-testid={"name-" + rowIndex}>
                    <span className={styles.Cell}>{prompt.name}</span>
                </TableCell>
                <LanguagesCell />
                <TableCell id={"audioFormatName-" + rowIndex} data-testid={"format-" + rowIndex}>
                    <span className={styles.Cell__large}>{prompt.audioFormat.name}</span>
                </TableCell>
                <TableCell id={"extensionName-" + rowIndex} data-testid={"extension-" + rowIndex}>
                    <span className={styles.Cell__small}>{prompt.audioFormat.type}</span>
                </TableCell>
                <TableCell id={"userName-" + rowIndex} data-testid={"user-" + rowIndex}>
                    <span className={styles.Cell}>{prompt.user.email}</span>
                </TableCell>
                <TableCell id={"createdAt-" + rowIndex} data-testid={"createdAt-" + rowIndex}>
                    <span className={styles.Cell}>
                        <TimeStamp value={prompt.createdAt} />
                    </span>
                </TableCell>
                <TableCell id={"updatedAt-" + rowIndex} data-testid={"updatedAt-" + rowIndex}>
                    <span className={styles.Cell}>
                        <TimeStamp value={prompt.updatedAt} />
                    </span>
                </TableCell>
                <TableCell id={"modifiedBy-" + rowIndex} data-testid={"modifiedBy-" + rowIndex}>
                    <span className={styles.Cell}>{prompt.modifiedBy.email}</span>
                </TableCell>
                <TableCell>
                    <span>
                        <IconButton
                            id={`edit-btn-${rowIndex}`}
                            data-testid={`edit-btn-${rowIndex}`}
                            className={"edit_prompt_button"}
                            size="small"
                            onClick={() => {
                                setEditing(true)
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    </span>
                </TableCell>
                <TableCell>
                    <span className={styles.Cell__small}>
                        {deleting && <CircularProgress size={"2em"} />}
                        {!deleting && (
                            <IconButton
                                size="small"
                                className={"delete_prompt_button"}
                                data-testid={`deleteBtn_${rowIndex}`}
                                id={`deleteBtn_${rowIndex}`}
                                onClick={() => {
                                    setDeleting(true)
                                    handleDelete()
                                }}
                            >
                                <DeleteOutline />
                            </IconButton>
                        )}
                    </span>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell className={styles.Cell__collapse} colSpan={12}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        {open && (
                            <PromptCard
                                mediaUUID={prompt.audioLocation}
                                hideCard={() => setOpen(false)}
                            />
                        )}
                    </Collapse>
                </TableCell>
            </TableRow>
            {editing && (
                <EditFileModal
                    open
                    onClose={() => {
                        setEditing(false)
                    }}
                    prompt={prompt}
                />
            )}
        </React.Fragment>
    )
}
